/**
 * @fileoverview Validación de geocodificación por código INE
 * @description Previene errores de desambiguación municipal validando
 *              que los resultados de CartoCiudad corresponden al municipio esperado
 * 
 * @author Proyecto PTEL - Normalizador de Coordenadas
 * @version 1.0.0
 * @date 2025-11-28
 */

import { getCodigoINE, getMunicipioCompleto } from '../../utils/codigosINEDerivados';
import { logRechazo } from './rejectionLogger';

// ============================================================================
// TIPOS
// ============================================================================

export interface CartoCiudadResponse {
  muni?: string;
  muniCode?: string;
  province?: string;
  lat?: number;
  lng?: number;
  state?: number;
  type?: string;
  address?: string;
}

export interface ValidationResult {
  valido: boolean;
  error?: string;
  codigoError?: 'fuera_andalucia' | 'provincia_incorrecta' | 'municipio_incorrecto' | 'sin_codigo_ine';
  detalles?: {
    codigoINEObtenido?: string;
    codigoINEEsperado?: string;
    municipioObtenido?: string;
    municipioEsperado?: string;
    provinciaObtenida?: string;
    provinciaEsperada?: string;
  };
}

// ============================================================================
// CONSTANTES
// ============================================================================

export const PROVINCIAS_ANDALUCIA: Record<string, string> = {
  '04': 'Almería',
  '11': 'Cádiz',
  '14': 'Córdoba',
  '18': 'Granada',
  '21': 'Huelva',
  '23': 'Jaén',
  '29': 'Málaga',
  '41': 'Sevilla'
};

export const CODIGOS_PROVINCIA_ANDALUCIA = Object.keys(PROVINCIAS_ANDALUCIA);

// ============================================================================
// FUNCIONES DE UTILIDAD
// ============================================================================

export function normalizarTexto(texto: string): string {
  if (!texto) return '';
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/\s+/g, ' ');
}

export function extraerCodigoProvincia(codigoINE: string | undefined): string | null {
  if (!codigoINE || codigoINE.length < 2) return null;
  return codigoINE.substring(0, 2);
}

export function esAndalucia(codigoINE: string | undefined): boolean {
  const codigoProvincia = extraerCodigoProvincia(codigoINE);
  return codigoProvincia !== null && CODIGOS_PROVINCIA_ANDALUCIA.includes(codigoProvincia);
}

export function getProvinciaDesdeINE(codigoINE: string | undefined): string | null {
  const codigoProvincia = extraerCodigoProvincia(codigoINE);
  if (!codigoProvincia) return null;
  return PROVINCIAS_ANDALUCIA[codigoProvincia] || null;
}

// ============================================================================
// FUNCIÓN PRINCIPAL DE VALIDACIÓN
// ============================================================================

export function validarResultadoCartoCiudad(
  response: CartoCiudadResponse,
  municipioEsperado: string,
  provinciaEsperada: string,
  codigoINEEsperado?: string,
  logearRechazos: boolean = true
): ValidationResult {
  
  const muniCode = response.muniCode;
  const municipioObtenido = response.muni || '';
  const provinciaObtenida = response.province || '';
  
  // 1. Verificar presencia de código INE
  if (!muniCode) {
    const result: ValidationResult = {
      valido: false,
      error: 'Respuesta sin código INE (campo muniCode)',
      codigoError: 'sin_codigo_ine',
      detalles: { municipioObtenido, municipioEsperado, provinciaObtenida, provinciaEsperada }
    };
    
    if (logearRechazos) {
      logRechazo({
        query: municipioEsperado,
        municipioEsperado,
        municipioObtenido,
        codigoINEEsperado: codigoINEEsperado || 'desconocido',
        codigoINEObtenido: 'N/A',
        motivo: 'sin_codigo_ine'
      });
    }
    
    return result;
  }
  
  // 2. Verificar que está en Andalucía
  if (!esAndalucia(muniCode)) {
    const result: ValidationResult = {
      valido: false,
      error: `Resultado fuera de Andalucía: ${municipioObtenido} (${provinciaObtenida})`,
      codigoError: 'fuera_andalucia',
      detalles: {
        codigoINEObtenido: muniCode,
        codigoINEEsperado,
        municipioObtenido,
        municipioEsperado,
        provinciaObtenida,
        provinciaEsperada
      }
    };
    
    if (logearRechazos) {
      logRechazo({
        query: municipioEsperado,
        municipioEsperado,
        municipioObtenido,
        codigoINEEsperado: codigoINEEsperado || 'desconocido',
        codigoINEObtenido: muniCode,
        motivo: 'fuera_andalucia'
      });
    }
    
    return result;
  }
  
  // 3. Verificar código de provincia
  const codigoProvinciaObtenido = extraerCodigoProvincia(muniCode);
  const codigoProvinciaEsperado = codigoINEEsperado 
    ? extraerCodigoProvincia(codigoINEEsperado)
    : obtenerCodigoProvinciaPorNombre(provinciaEsperada);
  
  if (codigoProvinciaEsperado && codigoProvinciaObtenido !== codigoProvinciaEsperado) {
    const result: ValidationResult = {
      valido: false,
      error: `Provincia incorrecta: obtenida ${getProvinciaDesdeINE(muniCode)} (${codigoProvinciaObtenido}), esperada ${provinciaEsperada} (${codigoProvinciaEsperado})`,
      codigoError: 'provincia_incorrecta',
      detalles: {
        codigoINEObtenido: muniCode,
        codigoINEEsperado,
        municipioObtenido,
        municipioEsperado,
        provinciaObtenida: getProvinciaDesdeINE(muniCode) || provinciaObtenida,
        provinciaEsperada
      }
    };
    
    if (logearRechazos) {
      logRechazo({
        query: municipioEsperado,
        municipioEsperado,
        municipioObtenido,
        codigoINEEsperado: codigoINEEsperado || 'desconocido',
        codigoINEObtenido: muniCode,
        motivo: 'provincia_incorrecta'
      });
    }
    
    return result;
  }
  
  // 4. Verificar código INE específico
  if (codigoINEEsperado && muniCode !== codigoINEEsperado) {
    const result: ValidationResult = {
      valido: false,
      error: `Código INE incorrecto: obtenido ${muniCode} (${municipioObtenido}), esperado ${codigoINEEsperado} (${municipioEsperado})`,
      codigoError: 'municipio_incorrecto',
      detalles: {
        codigoINEObtenido: muniCode,
        codigoINEEsperado,
        municipioObtenido,
        municipioEsperado,
        provinciaObtenida,
        provinciaEsperada
      }
    };
    
    if (logearRechazos) {
      logRechazo({
        query: municipioEsperado,
        municipioEsperado,
        municipioObtenido,
        codigoINEEsperado,
        codigoINEObtenido: muniCode,
        motivo: 'municipio_incorrecto'
      });
    }
    
    return result;
  }
  
  // 5. Validación exitosa
  return { valido: true };
}

function obtenerCodigoProvinciaPorNombre(nombreProvincia: string): string | null {
  const nombreNorm = normalizarTexto(nombreProvincia);
  
  for (const [codigo, nombre] of Object.entries(PROVINCIAS_ANDALUCIA)) {
    if (normalizarTexto(nombre) === nombreNorm) {
      return codigo;
    }
  }
  
  return null;
}

// ============================================================================
// FUNCIONES ADICIONALES
// ============================================================================

export async function validarCoordenadasConReverse(
  lat: number,
  lng: number,
  municipioEsperado: string
): Promise<ValidationResult> {
  try {
    const url = `https://www.cartociudad.es/geocoder/api/geocoder/reverseGeocode?lat=${lat}&lon=${lng}`;
    
    const response = await fetch(url);
    let text = await response.text();
    
    if (text.startsWith('callback(')) {
      text = text.slice(9, -1);
    }
    
    const data = JSON.parse(text);
    const municipioReal = normalizarTexto(data.muni || '');
    const esperadoNorm = normalizarTexto(municipioEsperado);
    
    const coincide = municipioReal.includes(esperadoNorm) || 
                     esperadoNorm.includes(municipioReal);
    
    if (coincide) {
      return { valido: true };
    } else {
      return {
        valido: false,
        error: `Reverse geocoding: coordenadas en ${data.muni}, esperado ${municipioEsperado}`,
        codigoError: 'municipio_incorrecto',
        detalles: { municipioObtenido: data.muni, municipioEsperado }
      };
    }
    
  } catch (error) {
    console.warn('[INE] Error en reverse geocoding:', error);
    return { valido: true };
  }
}

export function validarBatchResultados<T extends CartoCiudadResponse>(
  resultados: T[],
  municipio: string,
  provincia: string,
  codigoINE?: string
): T[] {
  return resultados.filter(resultado => {
    const validacion = validarResultadoCartoCiudad(resultado, municipio, provincia, codigoINE, true);
    return validacion.valido;
  });
}

// ============================================================================
// GUARD CROSS-MUNICODE AGREGADO POR INE
// (OBS-FT-XVAL-CARTO-CROSS-MUNI-DRIFT-GENERIC · 12-may-2026)
// ============================================================================

/**
 * Contador agregado de descartes por drift cross-muniCode, indexado por
 * `codigoINEEsperado`. Sirve para emitir telemetría `[CARTO-DRIFT]` de bloque
 * (no por feature) según el formato `[CARTO-DRIFT] {codINE}: discarded {n} drift responses`
 * especificado en `AUDIT_XVAL_SAN_SUBA_20260512.md §8.1`.
 *
 * Datos empíricos que motivan el guard:
 * - EDU: 92 / 1 686 (5,5 %) registros con `muniCode_carto != ine_bucket`.
 * - SAN-CS: 4 / 149 (2,68 %).
 * - SAN-HOSP: 6 / 88 (6,82 %).
 * Patrón común: `state_carto = 0`, `type_carto = 'portal'`, distancia geográfica
 * enorme, pero apariencia válida indetectable sin chequeo `muniCode`.
 *
 * El parámetro `muniCode` del endpoint CartoCiudad NO se respeta como filtro
 * upstream (comprobado empíricamente 11-may), por lo que el guard debe aplicarse
 * post-hoc en cliente.
 */
const cartoDriftCountersByINE: Map<string, number> = new Map();

/**
 * Motivos de drift cross-muniCode que el guard agregado contabiliza.
 * Excluye `sin_codigo_ine` (no es drift; es ausencia de campo) y
 * coincide con los códigos emitidos por `validarResultadoCartoCiudad`.
 */
const DRIFT_REASONS = new Set([
  'fuera_andalucia',
  'provincia_incorrecta',
  'municipio_incorrecto'
]);

/**
 * Guard cross-muniCode reutilizable. Encapsula `validarResultadoCartoCiudad`
 * y añade contabilidad agregada por bucket INE para telemetría diferida.
 *
 * Comportamiento:
 * - Si `codigoINEEsperado` está ausente: pasa-through (no hay referencia
 *   contra la que cotejar; búsquedas libres UI / sin contexto INE).
 * - Si `validarResultadoCartoCiudad` declara `valido === true`: acepta sin más.
 * - Si declara `valido === false` por motivo de drift: incrementa el contador
 *   agregado del INE esperado y devuelve `false`.
 * - Si declara `valido === false` por `sin_codigo_ine`: acepta (el caller
 *   decidirá según política específica; el guard de drift no aplica).
 *
 * @param response       Respuesta CartoCiudad a evaluar.
 * @param municipio      Municipio esperado (nombre legible).
 * @param provincia      Provincia esperada (nombre legible).
 * @param codigoINE      Código INE de 5 dígitos esperado (opcional).
 * @returns `true` si el resultado debe aceptarse aguas abajo; `false` si descartarse por drift.
 */
export function acceptCartoCiudadResult(
  response: CartoCiudadResponse,
  municipio: string | undefined,
  provincia: string | undefined,
  codigoINE?: string
): boolean {
  // Sin INE de referencia, no aplica el guard de drift.
  if (!codigoINE) {
    return true;
  }

  const validacion = validarResultadoCartoCiudad(
    response,
    municipio ?? '',
    provincia ?? '',
    codigoINE,
    true
  );

  if (validacion.valido) {
    return true;
  }

  if (validacion.codigoError && DRIFT_REASONS.has(validacion.codigoError)) {
    cartoDriftCountersByINE.set(
      codigoINE,
      (cartoDriftCountersByINE.get(codigoINE) ?? 0) + 1
    );
    return false;
  }

  // `sin_codigo_ine` u otros: no es drift cross-INE; delegar política al caller.
  return true;
}

/**
 * Vuelca el contador agregado de descartes por drift como `console.warn`
 * en el formato `[CARTO-DRIFT] {codINE}: discarded {n} drift responses`
 * (uno por bucket INE con ≥1 descarte). Limpia el contador tras el flush.
 *
 * Útil al cierre de una corrida batch o tras procesar un bloque homogéneo
 * por INE. Si no se llama explícitamente, los descartes individuales ya
 * quedan registrados en `rejectionLogger` por feature.
 *
 * @returns Cantidad total de descartes volcados.
 */
export function flushCartoDriftStats(): number {
  let total = 0;
  for (const [codigoINE, n] of cartoDriftCountersByINE.entries()) {
    if (n > 0) {
      console.warn(`[CARTO-DRIFT] ${codigoINE}: discarded ${n} drift responses`);
      total += n;
    }
  }
  cartoDriftCountersByINE.clear();
  return total;
}

/**
 * Lectura no destructiva del contador agregado (para tests y diagnóstico).
 * No emite logs ni limpia el estado.
 */
export function getCartoDriftStats(): Readonly<Record<string, number>> {
  const snapshot: Record<string, number> = {};
  for (const [k, v] of cartoDriftCountersByINE.entries()) {
    snapshot[k] = v;
  }
  return snapshot;
}

/**
 * Reset explícito del contador (para tests). Equivalente al cleanup
 * post-flush sin emitir logs.
 */
export function resetCartoDriftStats(): void {
  cartoDriftCountersByINE.clear();
}

// ============================================================================
// FUNCIÓN PRINCIPAL CRIT-03: Validación completa contra INE
// ============================================================================

/**
 * Umbral de distancia máxima al centroide municipal para considerar
 * las coordenadas como válidas dentro del municipio.
 * 
 * - ALTA confianza: < 5km del centroide
 * - MEDIA confianza: 5-15km del centroide
 * - BAJA confianza: > 15km del centroide
 */
export const UMBRAL_DISTANCIA_METROS = {
  ALTA: 5000,   // 5km
  MEDIA: 15000  // 15km
};

export interface INEValidationResult {
  codigoINE: string;
  nombreMunicipio: string;
  provincia: string;
  coordenadasDentroLimites: boolean;
  distanciaACentroide: number; // metros
  confianza: 'ALTA' | 'MEDIA' | 'BAJA';
  centroide?: { x: number; y: number };
}

/**
 * Calcula la distancia euclidiana entre dos puntos UTM en metros
 * @param x1 Coordenada X del punto 1 (UTM)
 * @param y1 Coordenada Y del punto 1 (UTM)
 * @param x2 Coordenada X del punto 2 (UTM)
 * @param y2 Coordenada Y del punto 2 (UTM)
 * @returns Distancia en metros
 */
export function calcularDistanciaACentroide(
  x1: number, y1: number,
  x2: number, y2: number
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Determina el nivel de confianza basado en la distancia al centroide
 */
function determinarConfianza(distancia: number): 'ALTA' | 'MEDIA' | 'BAJA' {
  if (distancia <= UMBRAL_DISTANCIA_METROS.ALTA) return 'ALTA';
  if (distancia <= UMBRAL_DISTANCIA_METROS.MEDIA) return 'MEDIA';
  return 'BAJA';
}

/**
 * Valida coordenadas contra el catálogo INE de municipios andaluces.
 * 
 * Esta función es el punto de entrada principal para CRIT-03.
 * Verifica que:
 * 1. El municipio declarado existe en el catálogo INE
 * 2. Las coordenadas están dentro de una distancia razonable del centroide
 * 3. Calcula un nivel de confianza basado en la proximidad
 * 
 * @param coordenadas Coordenadas UTM30 (EPSG:25830) a validar
 * @param municipioDeclarado Nombre del municipio declarado en el documento
 * @param provinciaDeclarada Nombre de la provincia declarada
 * @returns Resultado de validación con confianza y distancia
 */
export function validarContraINE(
  coordenadas: { x: number; y: number },
  municipioDeclarado: string,
  provinciaDeclarada: string
): INEValidationResult | null {
  
  // 1. Buscar código INE del municipio declarado
  const codigoINE = getCodigoINE(municipioDeclarado);
  
  if (!codigoINE) {
    console.warn(`[INE] Municipio no encontrado: ${municipioDeclarado}`);
    return null;
  }
  
  // 2. Verificar que es un municipio andaluz
  if (!esAndalucia(codigoINE)) {
    console.warn(`[INE] Municipio fuera de Andalucía: ${municipioDeclarado} (${codigoINE})`);
    return null;
  }
  
  // 3. Obtener datos completos del municipio (incluye centroide)
  const municipioData = getMunicipioCompleto(codigoINE);
  
  if (!municipioData) {
    console.warn(`[INE] No se encontraron datos para código: ${codigoINE}`);
    return null;
  }
  
  // 4. Verificar provincia
  const provinciaReal = municipioData.provincia;
  const provinciaNorm = normalizarTexto(provinciaDeclarada);
  const provinciaRealNorm = normalizarTexto(provinciaReal);
  
  if (provinciaNorm !== provinciaRealNorm) {
    console.warn(`[INE] Provincia no coincide: declarada ${provinciaDeclarada}, real ${provinciaReal}`);
  }
  
  // 5. Calcular distancia al centroide
  const distancia = calcularDistanciaACentroide(
    coordenadas.x, coordenadas.y,
    municipioData.x, municipioData.y
  );
  
  // 6. Determinar confianza
  const confianza = determinarConfianza(distancia);
  
  // 7. Determinar si está dentro de límites razonables
  const coordenadasDentroLimites = distancia <= UMBRAL_DISTANCIA_METROS.MEDIA;
  
  return {
    codigoINE,
    nombreMunicipio: municipioData.nombre,
    provincia: municipioData.provincia,
    coordenadasDentroLimites,
    distanciaACentroide: Math.round(distancia),
    confianza,
    centroide: { x: municipioData.x, y: municipioData.y }
  };
}

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

export default {
  validarResultadoCartoCiudad,
  validarCoordenadasConReverse,
  validarBatchResultados,
  validarContraINE,
  esAndalucia,
  getProvinciaDesdeINE,
  extraerCodigoProvincia,
  normalizarTexto,
  calcularDistanciaACentroide,
  PROVINCIAS_ANDALUCIA,
  CODIGOS_PROVINCIA_ANDALUCIA,
  UMBRAL_DISTANCIA_METROS
};
